import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, errorMessage, Wallet } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";

export function AutoTopUpPanel({ wallet, disabled }: { wallet?: Wallet; disabled: boolean }) {
  const qc = useQueryClient();
  const [enabled, setEnabled] = useState(false);
  const [threshold, setThreshold] = useState(50);
  const [amount, setAmount] = useState(100);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (wallet) {
      setEnabled(wallet.auto_topup_enabled);
      setThreshold(wallet.auto_topup_threshold_pence / 100);
      setAmount(wallet.auto_topup_amount_pence / 100);
    }
  }, [wallet]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/wallet/settings", { auto_topup_enabled: enabled, auto_topup_threshold_pence: Math.round(threshold * 100), auto_topup_amount_pence: Math.round(amount * 100) });
      toast.success("Auto top-up settings saved");
      qc.invalidateQueries({ queryKey: ["wallet"] });
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-10 lg:grid-cols-2" data-testid="auto-topup-panel">
      <div>
        <div className="eyebrow">Auto top-up</div>
        <h2 className="mt-2 font-serif text-2xl sm:text-3xl">Never run dry</h2>
        <p className="mt-3 text-sm text-neutral-600">After an order, if your account balance falls below the threshold we submit a Direct Debit for the top-up amount. Your balance can never go negative — orders are simply blocked until credit clears.</p>
      </div>
      <div className="space-y-6">
        <div className="flex items-center justify-between border border-border p-4">
          <span className="text-sm">Enable auto top-up</span>
          <Switch checked={enabled} onCheckedChange={setEnabled} disabled={disabled} data-testid="auto-topup-switch" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="eyebrow">When balance falls below</div>
            <div className="mt-2 flex items-center gap-2"><span className="font-serif text-xl">£</span><Input type="number" min={0} value={threshold} onChange={(e) => setThreshold(Number(e.target.value))} className="rounded-none bg-transparent tabular-nums" data-testid="auto-topup-threshold-input" /></div>
          </div>
          <div>
            <div className="eyebrow">Top up by</div>
            <div className="mt-2 flex items-center gap-2"><span className="font-serif text-xl">£</span><Input type="number" min={10} value={amount} onChange={(e) => setAmount(Number(e.target.value))} className="rounded-none bg-transparent tabular-nums" data-testid="auto-topup-amount-input" /></div>
          </div>
        </div>
        <Button onClick={save} disabled={busy} className="rounded-none px-8 hover-lift" data-testid="auto-topup-save-button">{busy ? "Saving…" : "Save settings"}</Button>
        {disabled && <p className="text-xs text-neutral-500">Connect a Direct Debit mandate to enable auto top-up.</p>}
      </div>
    </div>
  );
}
